import { createContext, useContext, useEffect, ReactNode, useState, useCallback } from 'react';
import supabase from '@/supabase';
import { FriendRequestWithUser, Friendship } from '@/hooks/use-realtime-friends';
import { useSession } from '@/hooks/useSessionHook';

interface RealtimeContextType {
  friendRequests: FriendRequestWithUser[];
  friendships: Friendship[];
  // Add other realtime data types as needed
}

const RealtimeContext = createContext<RealtimeContextType>({
  friendRequests: [],
  friendships: [],
});

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const [friendRequests, setFriendRequests] = useState<FriendRequestWithUser[]>([]);
  const [friendships, setFriendships] = useState<Friendship[]>([]);
  const { session } = useSession();
  const userId = session?.user?.id;
  
  // Function to fetch data
  const fetchData = useCallback(async () => {
    if (!userId) return;
    
    try {
      // Fetch friend requests
      const { data: requestsData, error: requestsError } = await supabase
        .from('friend_requests_with_profile')
        .select('*')
        .eq('recipient_id', userId)
        .eq('status', 'pending');
      
      if (requestsError) throw requestsError;
      setFriendRequests(requestsData || []);
      
      // Fetch existing friendships
      const { data: friendsData, error: friendsError } = await supabase
        .from('friends')
        .select('*')
        .eq('user_id', userId);
      
      if (friendsError) throw friendsError;
      setFriendships(friendsData || []);
    } catch (err) {
      console.error('Failed to load friends data', err);
    }
  }, [userId]);

  const setupFriendSubscriptions = useCallback(() => {
    const channelName = `realtime-global-${userId}`;
    console.log(`[Realtime] Creating channel: ${channelName}`);
    
    const channel = supabase.channel(channelName);

    const isOkSystemPayload = (payload: { status?: string } | null | undefined) =>
      payload?.status === 'ok';
    
      // Add debug logging for connection status
    // Add debug logging for system events
    channel
      .on('system' as any, { event: 'SUBSCRIBE' }, (payload: any) => { // eslint-disable-line
        console.log('[Realtime] Subscribe event', payload);
      })
      .on('system' as any, { event: 'CONNECTED' }, (payload: any) => { // eslint-disable-line
        console.log('[Realtime] Connected', payload);
      })
      .on('system' as any, { event: 'DISCONNECTED' }, (payload: any) => { // eslint-disable-line
        console.log('[Realtime] Disconnected', payload);
      })
      .on('system' as any, { event: 'ERROR' }, (payload: any) => { // eslint-disable-line
        if (isOkSystemPayload(payload)) {
          console.warn('[Realtime] Ignoring mislabeled ok system payload', payload);
          return;
        }
        console.error('[Realtime] Error event', payload);
      })
      .on('system' as any, { event: 'RECONNECT' }, (payload: any) => { // eslint-disable-line
        if (isOkSystemPayload(payload)) {
          return;
        }
        console.log('[Realtime] Reconnecting', payload);
      })
      // Specific event handlers
      .on(
        'postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'friend_requests',
          filter: `recipient_id=eq.${userId}`
        },
        async (payload) => {
          const { data: newRequest, error } = await supabase
            .from('friend_requests_with_profile')
            .select('*')
            .eq('id', payload.new.id)
            .single();

          if (error) {
            console.error('Error fetching new request:', error);
            return;
          }

          setFriendRequests(prev => [...prev, newRequest]);
        }
      )
      .on(
        'broadcast',
        { event: 'refetch-friends' },
        (payload) => {
          console.log('[Realtime Friends] Received broadcast refetch event', payload);
          if (payload.payload.userId === userId) {
            console.log('Refetching friends data due to broadcast');
            fetchData();
          }
        }
      )
      .on(
        'broadcast',
        { event: 'new-friend-request' },
        (payload) => {
          if (payload.payload.senderId && userId) {
            console.log('Received new-friend-request broadcast, refetching requests');
            fetchData();
          }
        }
      )
      .on(
        'postgres_changes',
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'friend_requests',
          filter: `recipient_id=eq.${userId}`
        },
        (payload) => {
          setFriendRequests(prev => 
            prev.map(req => 
              req.id === payload.new.id ? {...req, ...payload.new} : req
            )
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'friends',
          filter: `user_id=eq.${userId}`
        },
        async (payload) => {
          const newFriendship = payload.new as Friendship;
          setFriendships(prev => [...prev, newFriendship]);
          setFriendRequests(prev => 
            prev.filter(req => 
              !(
                (req.sender_id === newFriendship.friend_id && req.recipient_id === userId) ||
                (req.sender_id === userId && req.recipient_id === newFriendship.friend_id)
              )
            )
          );
        }
      );

    // Subscribe and log the result
    channel.subscribe((status, err) => {
      console.log(`[Realtime] Subscribe status: ${status}`);
      if (err) console.error('[Realtime] Subscribe error', err);
    });

    return channel;
  }, [userId, fetchData]);

  useEffect(() => {
    if (!supabase || !userId) return;

    // Fetch initial data
    fetchData();

    // Initialize all realtime subscriptions
    const friendSubscription = setupFriendSubscriptions();

    return () => {
      if (friendSubscription) {
        supabase.removeChannel(friendSubscription);
      }
    };
  }, [userId, fetchData, setupFriendSubscriptions]);

  return (
    <RealtimeContext.Provider value={{ friendRequests, friendships }}>
      {children}
    </RealtimeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useRealtime = () => useContext(RealtimeContext);
